import React from 'react';
import { Helmet } from 'react-helmet-async';

const StructuredData = ({ data }) => {
    if (!data) return null;
    
    return (
        <Helmet>
            <script type="application/ld+json">
                {JSON.stringify(data, null, 2)}
            </script>
        </Helmet>
    );
};

// Helper function to generate Person schema
export const generatePersonSchema = () => {
    return {
        "@context": "https://schema.org",
        "@type": "Person",
        "name": "Jacob Faulkner",
        "alternateName": "Ordos",
        "url": "https://jacobfaulkner.co.uk",
        "jobTitle": "3D Artist & Creative Technologist",
        "description": "Freelance 3D artist and creative technologist specializing in interactive installations, real-time experiences, and digital media.",
        "sameAs": [
            "https://www.ordos.co.uk",
            "https://www.instagram.com/ordos.studio"
        ],
        "knowsAbout": [
            "3D Modeling",
            "Interactive Installations",
            "Real-time Graphics",
            "Creative Technology",
            "Unity",
            "TouchDesigner",
            "Cinema 4D",
            "VR Development",
            "Projection Mapping",
            "3D Scanning"
        ],
        "address": {
            "@type": "PostalAddress",
            "addressCountry": "GB"
        }
    };
};

// Helper function to generate WebSite schema
export const generateWebSiteSchema = () => {
    return {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Jacob Faulkner Portfolio",
        "url": "https://jacobfaulkner.co.uk",
        "description": "Portfolio of Jacob Faulkner, a freelance 3D artist and creative technologist specializing in interactive installations, real-time experiences, and digital media.",
        "author": {
            "@type": "Person",
            "name": "Jacob Faulkner"
        },
        "publisher": {
            "@type": "Person",
            "name": "Jacob Faulkner"
        }
    };
};

// Helper function to generate CreativeWork schema for projects
export const generateCreativeWorkSchema = (project) => {
    const baseUrl = 'https://jacobfaulkner.co.uk';
    const slug = project.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const url = `${baseUrl}/work/${slug}`;
    
    return {
        "@context": "https://schema.org",
        "@type": "CreativeWork",
        "name": project.title,
        "description": project.description,
        "url": url,
        "creator": {
            "@type": "Person",
            "name": "Jacob Faulkner",
            "alternateName": "Ordos"
        },
        "dateCreated": project.year,
        "genre": project.category,
        "keywords": project.tags ? project.tags.join(", ") : "",
        "image": project.image ? (project.image.startsWith('http') ? project.image : `${baseUrl}${project.image}`) : undefined
    };
};

// Helper function to generate CollectionPage schema
export const generateCollectionPageSchema = () => {
    return {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Portfolio | Jacob Faulkner",
        "url": "https://jacobfaulkner.co.uk/work",
        "description": "Explore the portfolio of Jacob Faulkner, featuring 3D art, interactive installations, VR experiences, and creative technology projects.",
        "mainEntity": {
            "@type": "ItemList",
            "name": "Projects"
        }
    };
};

// Helper function to generate BreadcrumbList schema
export const generateBreadcrumbSchema = (items) => {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": items.map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": item.url
        }))
    };
};

// Helper function to generate FAQPage schema
export const generateFAQSchema = (faqs) => {
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
            }
        }))
    };
};

export default StructuredData;
